import { fetchPetsOffers } from '@/entities/insurance/api';
import { PetsOfferMapper } from '@/entities/insurance/mapper';
import { Env } from '@/shared/config/environment';
import { createPersistence } from '@/shared/lib/store';

const { mapPetsOffer, mapPetsOfferFromOrder } = PetsOfferMapper();
const { createAccessor, defineMutation } = createPersistence('pets-offers-');
const storage = createAccessor();

const state = {
  offers: {
    list: null,
    isLoading: false,
    error: false,
  },
  params: {
    showEarnings: storage.get('show-earnings', false),
  },
  activeFilters: storage.get('active-filters', { companies: [] }),
};

const filterOffersByCompanies = (offers, selectedCompanies) => {
  if (!offers || !selectedCompanies || selectedCompanies.length === 0) {
    return offers;
  }
  return offers.filter(offer => selectedCompanies.includes(offer.company.name));
};

const mutations = {
  SET_OFFERS_LIST(state, offers) {
    state.offers.list = offers;
  },
  SET_LOADING_OFFERS(state, isLoading) {
    state.offers.isLoading = isLoading;
  },
  SET_OFFERS_ERROR(state, error) {
    state.offers.error = error;
  },
  SET_SHOW_EARNINGS: defineMutation('params.showEarnings', 'show-earnings', false),
  SET_ACTIVE_FILTERS: defineMutation('activeFilters', 'active-filters', []),
};

const actions = {
  async calculateOffers({ dispatch, commit }, { startFrom, insurancePeriod, earnings }) {
    commit('SET_LOADING_OFFERS', true);
    commit('SET_OFFERS_ERROR', null);
    commit('SET_OFFERS_LIST', null);

    try {
      const offers = await fetchPetsOffers({ startFrom, insurancePeriod, earnings });
      const mapped = offers?.map(mapPetsOffer);

      commit('SET_OFFERS_LIST', mapped);

      return offers;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося отримати список пропозицій';

      commit('SET_OFFERS_ERROR', errorMessage);

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error,
          title: 'Не вдалося отримати список пропозицій',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_OFFERS', false);
    }
  },
  setShowEarnings({ commit }, showEarnings) {
    commit('SET_SHOW_EARNINGS', showEarnings);
  },
  setupFilter: ({ state, commit }, { filters, mode = 'replace' }) => {
    if (mode === 'replace') {
      commit('SET_ACTIVE_FILTERS', filters);
    } else {
      commit('SET_ACTIVE_FILTERS', { ...state.activeFilters, ...filters });
    }
  },

  hydrateOffersFromOrder({ commit }, { order }) {
    const offer = mapPetsOfferFromOrder(order);
    if (!offer) return;

    commit('SET_OFFERS_LIST', [offer]);
  },
};

const getters = {
  isLoadingOffers: state => state.offers.isLoading,
  isOffersReady: state => !state.offers.isLoading && state.offers.list !== null,
  offersListError: state => state.offers.error,

  getOffersList: state => state.offers.list,
  getOffersCount: (state, getters) => getters.getFilteredOffersList?.length || 0,
  getOffer: state => offerId => state.offers.list?.find(offer => offer.id === offerId) || null,

  getCompaniesList: state => {
    if (!state.offers.list) return [];
    const uniqueCompanies = [...new Set(state.offers.list.map(offer => offer.company.name))];
    return uniqueCompanies.map(companyName => ({
      id: `company-${companyName}`,
      name: companyName,
    }));
  },
  getFilteredOffersList: state => {
    return filterOffersByCompanies(state.offers.list, state.activeFilters?.companies || []);
  },

  getShowEarnings: state => state.params.showEarnings,
  getActiveFilters: state => state.activeFilters,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
